import { PageShell, DemoCTA } from "../components/PageShell";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Eye, EyeOff, XCircle } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { usePages } from "../translations/pages";

export default function SecuritePage() {
  const { language } = useLanguage();
  const p = usePages(language).securite;
  const { altitudes: ALTITUDES, jamais: JAMAIS, rgpd: RGPD, aiact: AIACT, secu: SECU, faq: FAQ } = p;
  return (
    <PageShell testid="securite-page">
      {/* Hero */}
      <div className="wrap-cayus mb-14">
        <p className="mono-cayus text-[#0047AB] mb-4">{p.eyebrow}</p>
        <h1 className="font-extrabold text-4xl lg:text-5xl text-[#1B1B1B] tracking-[-0.03em] mb-5">
          {p.title}
        </h1>
      </div>

      {/* Qui voit quoi */}
      <section className="wrap-cayus mb-20" data-testid="who-sees-what">
        <h2 className="font-bold text-3xl text-[#1B1B1B] tracking-[-0.025em] mb-10">{p.whoTitle}</h2>
        <div className="border border-[#C3CFE0] rounded-xl overflow-hidden divide-y divide-[#C3CFE0]">
          {ALTITUDES.map((r, i) => (
            <div key={i} className="grid md:grid-cols-[160px_1fr_1fr] gap-4 p-5 items-start">
              <p className="font-display font-semibold text-[#0A2342]">{r.a}</p>
              <div className="flex gap-2.5">
                <Eye className="w-4 h-4 text-[#10B981] flex-shrink-0 mt-0.5" />
                <p className="font-body-cayus text-sm text-[#4D4D4D]">{r.voit}</p>
              </div>
              <div className="flex gap-2.5">
                <EyeOff className="w-4 h-4 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                <p className="font-body-cayus text-sm text-[#4D4D4D]"><span className="mono-cayus text-[10px] text-[#F59E0B]">{p.neverLabel}</span><br />{r.jamais}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Ce qui n'existe jamais */}
      <section className="ink-section py-16 mb-20" data-testid="never-exists">
        <div className="wrap-cayus">
          <h2 className="font-bold text-3xl text-white tracking-[-0.025em] mb-10">
            {p.neverTitle}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {JAMAIS.map((j, i) => (
              <div key={i} className="bg-white/[0.04] border border-white/10 rounded-xl p-4 flex gap-2.5">
                <XCircle className="w-4 h-4 text-[#F87171] flex-shrink-0 mt-0.5" />
                <p className="font-body-cayus text-sm text-slate-200">{j}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Données & permissions */}
      <section className="wrap-cayus mb-20" data-testid="data-tiers">
        <h2 className="font-bold text-3xl text-[#1B1B1B] tracking-[-0.025em] mb-10">{p.dataTitle}</h2>
        <div className="grid sm:grid-cols-2 gap-6 mb-6">
          <div className="border border-[#C3CFE0] rounded-xl p-7 bg-white">
            <p className="mono-cayus text-[10px] text-[#0047AB] mb-2">{p.sideManager}</p>
            <p className="font-body-cayus text-sm text-[#4D4D4D] leading-relaxed">{p.sideManagerText}</p>
          </div>
          <div className="border border-[#C3CFE0] rounded-xl p-7 bg-[#F7F9FC]">
            <p className="mono-cayus text-[10px] text-[#0047AB] mb-2">{p.sideCompany}</p>
            <p className="font-body-cayus text-sm text-[#4D4D4D] leading-relaxed">{p.sideCompanyText}</p>
          </div>
        </div>
        <p className="mono-cayus text-xs text-[#0A2342] bg-[#F7F9FC] border border-[#C3CFE0] rounded-full text-center px-6 py-3">
          {p.promise}
        </p>
      </section>

      {/* RGPD / AI Act / Sécurité */}
      <section className="bg-[#F7F9FC] py-16 mb-20">
        <div className="wrap-cayus grid md:grid-cols-3 gap-6">
          {[[p.colRgpd, RGPD], [p.colAiAct, AIACT], [p.colSecu, SECU]].map(([title, items], i) => (
            <div key={i} className="bg-white border border-[#C3CFE0] rounded-xl p-6">
              <h3 className="font-display font-semibold text-lg text-[#0A2342] mb-4">{title}</h3>
              <ul className="flex flex-col gap-2.5">
                {items.map((it, j) => (
                  <li key={j} className="font-body-cayus text-sm text-[#4D4D4D] flex gap-2">
                    <span className="text-[#10B981] flex-shrink-0">✓</span>{it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="wrap-cayus mb-20" data-testid="securite-faq">
        <h2 className="font-bold text-3xl text-[#1B1B1B] tracking-[-0.025em] mb-8">{p.faqTitle}</h2>
        <Accordion type="single" collapsible className="w-full">
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={i} value={`f-${i}`}>
              <AccordionTrigger className="font-display font-semibold text-[#0A2342] text-left">{q}</AccordionTrigger>
              <AccordionContent className="font-body-cayus text-[#4D4D4D]">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <DemoCTA />
    </PageShell>
  );
}
